
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { CardContent } from '@/components/ui/card'
import { NodeContainer } from '../base/NodeContainer'
import { withNodeOptimization } from '../hoc/withNodeOptimization'
import { Icon } from '@/components/ui/icon'
import { Button } from '@/components/ui/button'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import {
  composeAllPureTemplate,
  estimateTokens,
  extractTemplateSlots,
  type ComposedPrompt,
  type CompositionStats,
  type ComposerConfig,
} from '@/lib/canvas/utils/promptComposer'
import type { GeneratedVariation } from '@/lib/canvas/utils/variationGenerator'
import { useSeriesDataStore } from '@/stores/useSeriesDataStore'

// ── Props ───────────────────────────────────────────────────────────────────

interface PromptComposerProps {
  id: string
  title?: string
  selected?: boolean
  width?: number
  height?: number
  onSelect?: () => void
  onHoverChange?: (hovered: boolean) => void
  onStateChange?: (updates: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => void
  state?: Record<string, unknown>
  connectedVariations?: GeneratedVariation[] | null
  connectedCategoryNames?: string[] | null
  connectedSystemPrompt?: string | null
}

type Tab = 'template' | 'preview'

// ── Main component ──────────────────────────────────────────────────────────

function PromptComposerComponent({
  id,
  title,
  selected = false,
  width,
  height,
  onSelect,
  onHoverChange,
  onStateChange,
  state = {},
  connectedVariations,
  connectedCategoryNames,
  connectedSystemPrompt,
}: PromptComposerProps) {
  // ── State ───────────────────────────────────────────────────────────────

  const [activeTab, setActiveTab] = useState<Tab>('template')
  const systemPrompt = connectedSystemPrompt ?? ''
  const [template, setTemplate] = useState<string>(
    () => (state.template as string) ?? '',
  )
  const [temperature, setTemperature] = useState<number>(
    () => (state.temperature as number) ?? 0.7,
  )
  const [maxTokensPerPrompt, setMaxTokensPerPrompt] = useState<number>(
    () => (state.maxTokensPerPrompt as number) ?? 200,
  )
  const [negativePromptTemplate, setNegativePromptTemplate] = useState<string>(
    () => (state.negativePromptTemplate as string) ?? '',
  )
  // Large data lives in external store (not React Flow node state)
  const composedPrompts = useSeriesDataStore(useCallback((s) => s.composedPrompts.get(id) ?? [], [id]))
  const storeSetComposedPrompts = useSeriesDataStore((s) => s.setComposedPrompts)
  const storeUpdateComposedPrompts = useSeriesDataStore((s) => s.updateComposedPrompts)

  const [compositionStatus, setCompositionStatus] = useState<'idle' | 'composing' | 'done'>(
    () => (state.compositionStatus as 'idle' | 'composing' | 'done') ?? 'idle',
  )
  const [compositionStats, setCompositionStats] = useState<CompositionStats | null>(
    () => (state.compositionStats as CompositionStats) ?? null,
  )
  const [progress, setProgress] = useState<{ composed: number; total: number } | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  // Virtual scroll for preview
  const [previewScrollTop, setPreviewScrollTop] = useState(0)
  const PROMPT_ROW_HEIGHT = 70
  const PROMPT_BUFFER = 5

  // ── Persist state ─────────────────────────────────────────────────────

  const isInitialMount = useRef(true)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    onStateChange?.((prev) => ({
      ...prev,
      template,
      temperature,
      maxTokensPerPrompt,
      negativePromptTemplate,
      composedPromptCount: composedPrompts.length,
      compositionStatus,
      compositionStats,
    }))
  }, [template, temperature, maxTokensPerPrompt, negativePromptTemplate, composedPrompts.length, compositionStatus, compositionStats, onStateChange])

  // ── Derived ───────────────────────────────────────────────────────────

  const variationCount = connectedVariations?.length ?? 0
  const composedCount = composedPrompts.filter((p) => p.status === 'composed' || p.status === 'manual-edit').length
  const flaggedCount = composedPrompts.filter((p) => p.status === 'flagged').length

  const config: ComposerConfig = useMemo(() => ({
    systemPrompt,
    template,
    temperature,
    maxTokensPerPrompt,
    negativePromptTemplate,
  }), [systemPrompt, template, temperature, maxTokensPerPrompt, negativePromptTemplate])

  const templateSlots = useMemo(() => extractTemplateSlots(template), [template])

  // ── Compose handlers ──────────────────────────────────────────────────

  const isComposing = useRef(false)

  const handleComposeAll = useCallback(async () => {
    if (!connectedVariations?.length || isComposing.current) return
    isComposing.current = true
    setCompositionStatus('composing')
    setProgress({ composed: 0, total: connectedVariations.length })

    const result = await composeAllPureTemplate(
      connectedVariations,
      config,
      (composed, total) => setProgress({ composed, total }),
    )
    storeSetComposedPrompts(id, result.prompts)
    setCompositionStats(result.stats)

    setCompositionStatus('done')
    setProgress(null)
    isComposing.current = false
  }, [connectedVariations, config])

  const handleComposeSample = useCallback(async () => {
    if (!connectedVariations?.length || isComposing.current) return
    const sample = [...connectedVariations].sort(() => Math.random() - 0.5).slice(0, 10)
    isComposing.current = true
    setCompositionStatus('composing')
    setProgress({ composed: 0, total: sample.length })

    const result = await composeAllPureTemplate(
      sample,
      config,
      (composed, total) => setProgress({ composed, total }),
    )
    storeSetComposedPrompts(id, result.prompts)
    setCompositionStats(result.stats)
    setCompositionStatus('done')
    setProgress(null)
    isComposing.current = false
  }, [connectedVariations, config])

  // ── Prompt editing ────────────────────────────────────────────────────

  const handleEditPrompt = useCallback((index: number, newPrompt: string) => {
    storeUpdateComposedPrompts(id, (prev) =>
      prev.map((p) =>
        p.index === index
          ? { ...p, prompt: newPrompt, status: 'manual-edit' as const, manualOverride: newPrompt, tokenCount: estimateTokens(newPrompt) }
          : p
      )
    )
  }, [id, storeUpdateComposedPrompts])

  const handleFlagPrompt = useCallback((index: number) => {
    storeUpdateComposedPrompts(id, (prev) =>
      prev.map((p) =>
        p.index === index
          ? { ...p, status: p.status === 'flagged' ? 'composed' as const : 'flagged' as const }
          : p
      )
    )
  }, [id, storeUpdateComposedPrompts])

  const scrollRef = useRef<HTMLDivElement>(null)

  // ── Status text ───────────────────────────────────────────────────────

  const statusText = useMemo(() => {
    if (!connectedVariations?.length) return 'No variations connected'
    if (compositionStatus === 'composing' && progress) {
      return `Composing ${progress.composed}/${progress.total}...`
    }
    if (compositionStatus === 'done' && compositionStats) {
      const flagInfo = flaggedCount > 0 ? ` · ${flaggedCount} flagged` : ''
      return `${composedCount} prompts ready${flagInfo}`
    }
    return `Ready · ${variationCount} variations`
  }, [connectedVariations, compositionStatus, progress, compositionStats, composedCount, flaggedCount, variationCount])

  // ── Filtered prompts for preview ──────────────────────────────────────

  const filteredPrompts = useMemo(() => {
    if (!searchQuery.trim()) return composedPrompts
    const q = searchQuery.toLowerCase()
    return composedPrompts.filter((p) => p.prompt.toLowerCase().includes(q))
  }, [composedPrompts, searchQuery])

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <NodeContainer
      nodeId={id}
      title={title || 'Prompt Composer'}
      icon="pen-line"
      selected={selected}
      nodeType="text"
      width={width}
      onSelect={onSelect}
      onHoverChange={onHoverChange}
      onStateChange={onStateChange}
      state={state}
    >
      <CardContent className="relative flex flex-col flex-1 p-0 overflow-hidden">
        {/* Status header */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/50">
          <span className="text-[11px] text-muted-foreground tabular-nums">{statusText}</span>
          <Popover>
            <PopoverTrigger asChild>
              <button className="p-0.5 rounded hover:bg-muted transition-colors nodrag">
                <Icon name="settings" size="sm" className="text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent side="bottom" align="end" className="w-72 p-3 nodrag nopan nowheel">
              <div className="space-y-3">
                {/* Temperature */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] text-foreground">Temperature</span>
                    <span className="text-[10px] text-muted-foreground tabular-nums">{temperature.toFixed(1)}</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.1}
                    value={temperature}
                    onChange={(e) => setTemperature(Number(e.target.value))}
                    className="w-full h-1.5 rounded-full appearance-none bg-muted cursor-pointer nodrag nopan"
                  />
                  <div className="flex justify-between mt-0.5">
                    <span className="text-[9px] text-muted-foreground">Consistent</span>
                    <span className="text-[9px] text-muted-foreground">Creative</span>
                  </div>
                </div>

                {/* Max tokens per prompt */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] text-foreground">Max tokens per prompt</span>
                    <span className="text-[10px] text-muted-foreground tabular-nums">{maxTokensPerPrompt}</span>
                  </div>
                  <input
                    type="range"
                    min={50}
                    max={500}
                    step={10}
                    value={maxTokensPerPrompt}
                    onChange={(e) => setMaxTokensPerPrompt(Number(e.target.value))}
                    className="w-full h-1.5 rounded-full appearance-none bg-muted cursor-pointer nodrag nopan"
                  />
                  <div className="flex justify-between mt-0.5">
                    <span className="text-[9px] text-muted-foreground">Concise</span>
                    <span className="text-[9px] text-muted-foreground">Detailed</span>
                  </div>
                </div>

                {/* Cost estimate */}
                {connectedVariations && connectedVariations.length > 0 && (
                  <div className="rounded-md bg-muted p-2">
                    <span className="text-[10px] text-muted-foreground">
                      Estimated for {connectedVariations.length} prompts: ~{(connectedVariations.length * (maxTokensPerPrompt + estimateTokens(systemPrompt))).toLocaleString()} tokens
                    </span>
                  </div>
                )}

                {/* Negative prompt template */}
                <div>
                  <span className="text-[11px] text-foreground block mb-1">Negative prompt template</span>
                  <textarea
                    value={negativePromptTemplate}
                    onChange={(e) => setNegativePromptTemplate(e.target.value)}
                    placeholder="Shared negative prompt (supports {category} slots)..."
                    className="w-full min-h-[60px] resize-none rounded-md border border-border/50 bg-muted p-2 text-xs outline-none placeholder:text-muted-foreground/50 nodrag nopan nowheel"
                    spellCheck={false}
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border/50">
          {(['template', 'preview'] as Tab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                'flex-1 py-1 text-[10px] font-medium transition-colors capitalize',
                activeTab === tab
                  ? 'text-foreground border-b-2 border-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {tab === 'preview' && composedCount > 0 ? `Preview (${composedCount})` : tab}
            </button>
          ))}
        </div>

        {/* ── Template Tab ─────────────────────────────────────────────── */}
        {activeTab === 'template' && (
          <div className="flex flex-col flex-1 overflow-hidden">
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto scrollbar-thin p-2"
              style={{ maxHeight: '340px' }}
            >
              <textarea
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                placeholder={`Enter template with {category_name} slots...&#10;&#10;Example: Tight portrait of {archetype}, {eye_treatment}, in {lighting} lighting...`}
                className="w-full min-h-[140px] resize-none border-0 bg-transparent text-xs outline-none placeholder:text-muted-foreground/50 nodrag nopan"
                spellCheck={false}
              />

              {/* Detected slots */}
              {templateSlots.length > 0 && (
                <div className="mt-2">
                  <span className="text-[10px] text-muted-foreground">Detected slots: </span>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {templateSlots.map((slot) => {
                      const matched = connectedCategoryNames?.some(
                        (n) => n.toLowerCase().replace(/[\s_-]+/g, '_') === slot.toLowerCase().replace(/[\s_-]+/g, '_')
                      )
                      return (
                        <span
                          key={slot}
                          className={cn(
                            'inline-block rounded px-1 py-0.5 text-[9px]',
                            matched ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                          )}
                        >
                          {`{${slot}}`}
                          {!matched && ' ?'}
                        </span>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Available categories */}
              {connectedCategoryNames && connectedCategoryNames.length > 0 && (
                <div className="mt-2">
                  <span className="text-[10px] text-muted-foreground">Available categories: </span>
                  <span className="text-[10px] text-foreground">
                    {connectedCategoryNames.map((n) => `{${n.toLowerCase().replace(/\s+/g, '_')}}`).join(', ')}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Preview Tab ──────────────────────────────────────────────── */}
        {activeTab === 'preview' && (
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Compose buttons */}
            <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-border/50">
              <Button
                variant="default"
                size="sm"
                onClick={handleComposeAll}
                disabled={!connectedVariations?.length || compositionStatus === 'composing'}
                className="h-6 text-[10px] gap-1 flex-1"
              >
                {compositionStatus === 'composing' ? (
                  <Icon name="loader" size="sm" className="animate-spin" />
                ) : (
                  <Icon name="pen-line" size="sm" />
                )}
                Compose All ({variationCount})
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleComposeSample}
                disabled={!connectedVariations?.length || compositionStatus === 'composing'}
                className="h-6 text-[10px] gap-1 shrink-0"
              >
                Sample 10
              </Button>
            </div>

            {/* Search */}
            {composedPrompts.length > 0 && (
              <div className="flex items-center gap-1.5 px-2 py-1 border-b border-border/50">
                <Icon name="search" size="sm" className="text-muted-foreground shrink-0" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search prompts..."
                  className="flex-1 bg-transparent text-[11px] outline-none placeholder:text-muted-foreground/50 nodrag nopan"
                />
                {searchQuery && (
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {filteredPrompts.length}/{composedPrompts.length}
                  </span>
                )}
              </div>
            )}

            {/* Prompt list (virtual scroll) */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto scrollbar-thin"
              style={{ maxHeight: '320px' }}
              onScroll={(e) => setPreviewScrollTop(e.currentTarget.scrollTop)}
            >
              {filteredPrompts.length > 0 ? (() => {
                const containerH = scrollRef.current?.clientHeight ?? 320
                const totalH = filteredPrompts.length * PROMPT_ROW_HEIGHT
                const startIdx = Math.max(0, Math.floor(previewScrollTop / PROMPT_ROW_HEIGHT) - PROMPT_BUFFER)
                const endIdx = Math.min(filteredPrompts.length, Math.ceil((previewScrollTop + containerH) / PROMPT_ROW_HEIGHT) + PROMPT_BUFFER)
                const visible = filteredPrompts.slice(startIdx, endIdx)

                return (
                  <div style={{ height: totalH, position: 'relative' }}>
                    {visible.map((prompt, i) => (
                      <div
                        key={prompt.index}
                        className="group border-b border-border/30 px-2 py-1 absolute left-0 right-0 overflow-hidden"
                        style={{ top: (startIdx + i) * PROMPT_ROW_HEIGHT, height: PROMPT_ROW_HEIGHT }}
                      >
                        {/* Header */}
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-muted-foreground tabular-nums">#{prompt.index}</span>
                          <span className={cn(
                            'text-[9px] rounded px-1',
                            prompt.status === 'flagged' ? 'bg-amber-500/20 text-amber-400' :
                            prompt.status === 'manual-edit' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-emerald-500/20 text-emerald-400'
                          )}>
                            {prompt.status}
                          </span>
                          <span className="text-[9px] text-muted-foreground ml-auto tabular-nums">~{prompt.tokenCount} tok</span>
                          <button
                            onClick={() => handleFlagPrompt(prompt.index)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Icon name="flag" size="sm" className={prompt.status === 'flagged' ? 'text-amber-400' : 'text-muted-foreground hover:text-amber-400'} />
                          </button>
                        </div>
                        {/* Prompt text (read-only display, click to edit) */}
                        <p className="text-[10px] text-foreground mt-0.5 line-clamp-3 leading-tight">
                          {prompt.manualOverride ?? prompt.prompt}
                        </p>
                      </div>
                    ))}
                  </div>
                )
              })() : (
                <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">
                  {connectedVariations?.length
                    ? 'Click Compose All to generate prompts'
                    : 'Connect a Variation Generator to get started'}
                </div>
              )}
            </div>

            {/* Stats footer */}
            {compositionStats && (
              <div className="flex items-center justify-between px-2 py-1 border-t border-border/50">
                <span className="text-[10px] text-muted-foreground tabular-nums">
                  {compositionStats.totalComposed} composed · ~{compositionStats.averageTokensPerPrompt} avg tokens
                </span>
                {compositionStats.manualEdits > 0 && (
                  <span className="text-[10px] text-blue-400 tabular-nums">
                    {compositionStats.manualEdits} edited
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </NodeContainer>
  )
}

export const PromptComposer = withNodeOptimization(PromptComposerComponent, {
  compareProps: ['id', 'title', 'selected', 'state', 'connectedVariations', 'connectedCategoryNames', 'connectedSystemPrompt'],
})
